import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Highlight, Snippet, useHits, useSearchBox } from "react-instantsearch";
import {
  Container,
  Pagination,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import { getCallRecordingTranscriptions } from "../../api/transcriptions";
import { useAuth } from "../../contexts/AuthContext";
import PageLoader from "../PageLoader";
import CallTranscriptionRow from "./CallTranscriptionRow";

const CallTranscriptions = ({
  searchMode,
  setInfoModal,
  children,
  searchParams,
}) => {
  const [page, setPage] = useState("1");

  useEffect(() => {
    setPage("1");
  }, [searchParams]);

  const queryClient = useQueryClient();
  const { apiUser } = useAuth();
  const { query } = useSearchBox();
  const hits = useHits();

  const transcriptionsQuery = useQuery(
    ["call-transcriptions", page, searchParams],
    () => {
      return getCallRecordingTranscriptions("", {
        page: page,
        hydrate: "0",
        ...searchParams,
      });
    },
    {
      keepPreviousData: true,
      staleTime: 60000,
      refetchIntervalInBackground: true,
      refetchInterval: (data) => {
        let interval = false;
        data?.data.data.forEach((i) => {
          if (["SUMMARISING", "TRANSCRIBING"].includes(i?.status.value)) {
            queryClient.invalidateQueries({ queryKey: ["call-recordings"] });
            interval = 30 * 1000;
          }
        });
        return interval;
      },
    },
  );

  const handlePaginationChange = (e, { activePage }) => {
    setPage(activePage);
  };

  if (transcriptionsQuery.isLoading) return <PageLoader>Loading...</PageLoader>;

  return (
    <Container style={{ marginTop: "20px" }} fluid>
      {searchMode === "simple" && query.length > 0 ? (
        <Table basic="very" inverted>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Extension</TableHeaderCell>
              <TableHeaderCell width={1}>Number</TableHeaderCell>
              <TableHeaderCell width={1}>Direction</TableHeaderCell>
              <TableHeaderCell width={2}>Start Time</TableHeaderCell>
              <TableHeaderCell width={2}>End Time</TableHeaderCell>
               <TableHeaderCell width={1}>Timezone</TableHeaderCell>
               <TableHeaderCell width={1}>Duration</TableHeaderCell>
               <TableHeaderCell width={5}>Summary</TableHeaderCell>
               <TableHeaderCell></TableHeaderCell>
               <TableHeaderCell>View</TableHeaderCell>
             </TableRow>
           </TableHeader>

           <TableBody>
             {hits?.items.map((i) => (
               <CallTranscriptionRow
                 id={i.sqid}
                 key={`transcript-row-${i.sqid}`}
                 status={i.status}
                 hasSummary={true}
                 summary={<Snippet attribute="transcription" hit={i} />}
                 sentiment={i.sentiment}
                 direction={<Highlight attribute="direction" hit={i} />}
                 number={<Highlight attribute="tel_number" hit={i} />}
                  start={new Date(i.start_timestamp * 1000).toLocaleString(
                    apiUser.locale,
                    { timeZone: i.effective_timezone },
                  )}
                  end={new Date(i.end_timestamp * 1000).toLocaleString(
                    apiUser.locale,
                    { timeZone: i.effective_timezone },
                  )}
                 extension={<Highlight attribute="user_ext" hit={i} />}
                 duration={i.duration}
                 timezone={i.effective_timezone}
                 setInfoModal={setInfoModal}
               />
            ))}

            {hits?.hits.length === 0 && <div>No transcriptions available.</div>}
          </TableBody>
        </Table>
      ) : (
        <>
          {transcriptionsQuery?.data.data.data.length > 0 ? (
            <Table basic="very" inverted>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Extension</TableHeaderCell>
                  <TableHeaderCell width={1}>Number</TableHeaderCell>
                  <TableHeaderCell width={1}>Direction</TableHeaderCell>
                  <TableHeaderCell width={2}>Start Time</TableHeaderCell>
                  <TableHeaderCell width={2}>End Time</TableHeaderCell>
                   <TableHeaderCell width={1}>Timezone</TableHeaderCell>
                   <TableHeaderCell width={1}>Duration</TableHeaderCell>
                   <TableHeaderCell width={5}>Summary</TableHeaderCell>
                   <TableHeaderCell></TableHeaderCell>
                   <TableHeaderCell>View</TableHeaderCell>
                 </TableRow>
               </TableHeader>

               <TableBody>
                 {transcriptionsQuery?.data.data.data.map((i) => {
                   return (
                     <CallTranscriptionRow
                       id={i.sqid}
                       key={`transcript-row-${i.sqid}`}
                       status={i.status}
                       hasSummary={i.has_summary}
                       summary={i.summary}
                       sentiment={i.sentiment}
                       direction={i.direction}
                       number={i.tel_number}
                       start={i.start}
                       end={i.end}
                       extension={i.user_ext}
                       duration={i.duration}
                       timezone={i.effective_timezone}
                       setInfoModal={setInfoModal}
                     />
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div>No transcriptions available.</div>
          )}

          <div
            style={{ width: "100%", textAlign: "center", marginTop: "25px" }}
          >
            <Pagination
              inverted
              activePage={page}
              totalPages={transcriptionsQuery.data.data.meta.last_page}
              onPageChange={handlePaginationChange}
            />
          </div>
        </>
      )}
      {children}
    </Container>
  );
};

export default CallTranscriptions;
